/**
 * GameRegistry - Registre central des systèmes de jeu
 * Identique à Flutter GameRegistry
 */

import React from 'react';
import {SimpleSystem} from './simple/SimpleSystem';
import {PlayerStep} from '../models/PlayerStep';
import {GameState} from '../models/GameState';
import {ActionType} from './types/ActionType';
import {EventType} from './types/EventType';
import {AttributeType} from './types/AttributeType';
import {ExtraFieldType} from './types/ExtraFieldType';
import {StepType} from './types/StepType';
import {ConditionType, ConditionTypeBuilder} from './types/ConditionType';
import {TrueConditionType} from './simple/conditions/TrueConditionType';

export type StepBuilder = (step: PlayerStep) => React.ReactElement;
export type SlotRenderer = (gameState: GameState | null, slotIndex: number) => React.ReactElement | null;

class GameRegistry {
  // === Systems ===
  private static _systems: Map<string, SimpleSystem> = new Map();
  private static _conditionTypeBuilders: Map<string, ConditionTypeBuilder> = new Map();
  private static _stepBuilders: Map<string, StepBuilder> = new Map();
  private static _slotRenderers: Map<string, SlotRenderer> = new Map();
  private static _defaultSystem: SimpleSystem | null = null;
  private static _defaultConditionTypeBuilder: ConditionTypeBuilder = (attribute) =>
    new TrueConditionType(attribute);
  private static _defaultStepBuilder: StepBuilder | null = null;
  private static _defaultSlotRenderer: SlotRenderer | null = null;

  /**
   * Récupère le système par défaut (lazy initialization pour éviter les dépendances circulaires)
   */
  private static _getDefaultSystem(): SimpleSystem {
    if (!this._defaultSystem) {
      // Créer une instance de SimpleSystem sans déclencher l'initialisation complète
      // L'enregistrement des conditions sera fait plus tard via App.tsx
      this._defaultSystem = new SimpleSystem();
    }
    return this._defaultSystem;
  }

  // === Types ===
  private static _actions: Map<string, ActionType> = new Map();
  private static _events: Map<string, EventType> = new Map();
  private static _attributes: Map<string, AttributeType> = new Map();
  private static _adventureExtraFields: Map<string, ExtraFieldType> = new Map();
  private static _stepExtraFields: Map<string, ExtraFieldType> = new Map();
  private static _stepTypes: Map<string, StepType> = new Map();

  /**
   * Enregistre un système de jeu sous un type donné
   * Le type est stocké dans le système pour que getAdventureExtraFieldTypes() puisse l'utiliser
   */
  static registerSystem(type: string, system: SimpleSystem): void {
    // Stocker le type dans le système
    (system as any)._systemType = type;
    this._systems.set(type, system);
    console.log(`[GameRegistry] Registered system: ${type}`);
  }

  /**
   * Enregistre un type de condition
   */
  static registerConditionType(type: string, builder: ConditionTypeBuilder): void {
    this._conditionTypeBuilders.set(type, builder);
  }

  /**
   * Enregistre une action (utilise le typeName de l'action)
   */
  static registerActionType(action: ActionType): void {
    this._actions.set(action.typeName, action);
    console.log(`[GameRegistry] Registered action: ${action.typeName}`);
  }

  /**
   * Enregistre un événement (utilise le typeName de l'événement)
   */
  static registerEventType(event: EventType): void {
    this._events.set(event.typeName, event);
  }

  /**
   * Enregistre un attribut (utilise le typeName de l'attribut)
   */
  static registerAttributeType(attribute: AttributeType): void {
    this._attributes.set(attribute.typeName, attribute);
  }

  /**
   * Enregistre un champ supplémentaire pour l'aventure (utilise le typeName du champ)
   */
  static registerAdventureExtraFieldType(extraField: ExtraFieldType): void {
    this._adventureExtraFields.set(extraField.typeName, extraField);
    console.log(`[GameRegistry] Registered adventure extraField: ${extraField.typeName}`);
  }

  /**
   * Enregistre un champ supplémentaire pour les steps (utilise le typeName du champ)
   */
  static registerStepExtraFieldType(extraField: ExtraFieldType): void {
    this._stepExtraFields.set(extraField.typeName, extraField);
    console.log(`[GameRegistry] Registered step extraField: ${extraField.typeName}`);
  }

  /**
   * @deprecated Utiliser registerAdventureExtraFieldType ou registerStepExtraFieldType
   * Enregistre un champ supplémentaire (utilise le typeName du champ)
   */
  static registerExtraFieldType(extraField: ExtraFieldType): void {
    // Par défaut, enregistrer comme adventureExtraField pour compatibilité
    this.registerAdventureExtraFieldType(extraField);
  }

  /**
   * Enregistre un type de step (utilise le typeName du step)
   */
  static registerStepType(stepType: StepType): void {
    this._stepTypes.set(stepType.typeName, stepType);
  }

  /**
   * Récupère le système ou le système par défaut
   */
  static getSystem(type: string): SimpleSystem {
    console.log(`[GameRegistry] getSystem called with type: "${type}"`);
    console.log(`[GameRegistry] Available systems: ${Array.from(this._systems.keys())}`);

    // Recherche exacte uniquement (pas de fallback/compatibilité)
    const system = this._systems.get(type);

    if (!system) {
      console.log(
        `[GameRegistry] System "${type}" not found in _systems, using default system`,
      );
      return this._getDefaultSystem();
    } else {
      console.log(`[GameRegistry] Found system "${type}"`);
      return system;
    }
  }

  /**
   * Récupère un type de condition
   */
  static getConditionType(type: string, attribute?: string): ConditionType {
    const builder = this._conditionTypeBuilders.get(type) || this._defaultConditionTypeBuilder;
    return builder(attribute);
  }

  /**
   * Récupère un builder de step
   */
  static getStepBuilder(type: string): StepBuilder {
    // Recherche exacte uniquement (pas de fallback/compatibilité)
    let builder = this._stepBuilders.get(type);
    
    // Si pas trouvé, utiliser le default
    if (!builder) {
      builder = this._defaultStepBuilder;
    }
    if (!builder) {
      console.error(`[GameRegistry] No step builder registered for type "${type}". Available: ${Array.from(this._stepBuilders.keys()).join(', ')}`);
      throw new Error(`No step builder registered for type ${type}`);
    }
    return builder;
  }

  /**
   * Récupère une action par son type
   */
  static getActionType(type: string): ActionType | undefined {
    return this._actions.get(type);
  }

  /**
   * Récupère un événement par son type
   */
  static getEventType(type: string): EventType | undefined {
    return this._events.get(type);
  }

  /**
   * Récupère un attribut par son type
   */
  static getAttributeType(type: string): AttributeType | undefined {
    return this._attributes.get(type);
  }

  /**
   * Récupère un champ supplémentaire par son type (cherche dans adventure et step extraFields)
   */
  static getExtraFieldType(type: string): ExtraFieldType | undefined {
    // Chercher d'abord dans les adventureExtraFields, puis dans les stepExtraFields
    const field = this._adventureExtraFields.get(type) || this._stepExtraFields.get(type);
    if (!field) {
      const adventureTypes = Array.from(this._adventureExtraFields.keys());
      const stepTypes = Array.from(this._stepExtraFields.keys());
      console.log(
        `[GameRegistry] ExtraField type "${type}" not found. Available adventure types: ${adventureTypes.join(', ')}. Available step types: ${stepTypes.join(', ')}`,
      );
    }
    return field;
  }

  /**
   * Getter pour tous les extraFields (adventure + step) - pour compatibilité
   */
  static get extraFields(): ExtraFieldType[] {
    const adventure = Array.from(this._adventureExtraFields.values());
    const step = Array.from(this._stepExtraFields.values());
    return [...adventure, ...step];
  }

  /**
   * Récupère un type de step par son type
   */
  static getStepType(type: string): StepType | undefined {
    return this._stepTypes.get(type);
  }

  /**
   * Enregistre un builder pour un type d'étape
   */
  static registerStepBuilder(type: string, builder: StepBuilder): void {
    this._stepBuilders.set(type, builder);
  }

  /**
   * Définit le builder de step par défaut
   */
  static setDefaultStepBuilder(builder: StepBuilder): void {
    this._defaultStepBuilder = builder;
  }

  /**
   * Enregistre un renderer de slot pour un système de jeu
   */
  static registerSlotRenderer(systemType: string, renderer: SlotRenderer): void {
    this._slotRenderers.set(systemType, renderer);
    console.log(`[GameRegistry] Registered slot renderer for system: ${systemType}`);
  }

  /**
   * Définit le renderer de slot par défaut
   */
  static setDefaultSlotRenderer(renderer: SlotRenderer): void {
    this._defaultSlotRenderer = renderer;
  }

  /**
   * Récupère le renderer de slot pour un système de jeu
   */
  static getSlotRenderer(systemType: string): SlotRenderer | null {
    const renderer = this._slotRenderers.get(systemType);
    if (renderer) {
      return renderer;
    }
    return this._defaultSlotRenderer;
  }

  /**
   * Construit une étape avec le builder approprié
   */
  static buildStep(step: PlayerStep): React.ReactElement {
    const builder = this.getStepBuilder(step.type);
    return builder(step);
  }

  /**
   * Récupère toutes les actions
   */
  static get actions(): ActionType[] {
    return Array.from(this._actions.values());
  }

  /**
   * Récupère tous les événements
   */
  static get events(): EventType[] {
    return Array.from(this._events.values());
  }

  /**
   * Récupère tous les attributs
   */
  static get attributes(): AttributeType[] {
    return Array.from(this._attributes.values());
  }

  /**
   * Récupère tous les champs supplémentaires
   */
  static get extraFields(): ExtraFieldType[] {
    return Array.from(this._extraFields.values());
  }

  /**
   * Récupère la liste des types d'étapes enregistrés (pour debug)
   */
  static get registeredStepTypes(): string[] {
    return Array.from(this._stepBuilders.keys());
  }
}

export default GameRegistry;
